import React, { useState, useEffect } from "react";
import Api from "../../../Api/api";
import { toast } from "react-toastify";
import { AES, enc } from "crypto-js";
import { useStateValue } from "../../../states/StateProvider";
import { BeatLoader } from "react-spinners";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EmailReports = () => {
    const [{ secretKey }] = useStateValue();
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Check if user has access (test@test.dd only)
    const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
    const user = JSON.parse(decryptedBytes.toString(enc.Utf8));
    const hasAccess = user?.email === 's_nothegger@web.de' || user?.email === 'kbit.business@gmx.de';

    useEffect(() => {
        // Set default dates to yesterday
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
    }, []);

    const handleSendReport = async () => {
        if (!startDate || !endDate) {
            toast.error('Bitte wählen Sie Start- und Enddatum aus');
            return;
        }

        if (startDate > endDate) {
            toast.error('Startdatum darf nicht nach dem Enddatum liegen');
            return;
        }

        setLoading(true);

        try {
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // If start and end dates are the same, send single date request
            if (startDate.getTime() === endDate.getTime()) {
                const requestData = {
                    date: formatDate(startDate),
                    email: user.email
                };

                const response = await Api().post('/sendDailyEmailReport', requestData);

                if (response.data.success) {
                    toast.success('E-Mail-Bericht erfolgreich gesendet');

                    // Show additional info if available
                    if (response.data.output) {
                        console.log('Report Output:', response.data.output);
                        toast.info(`Bericht für ${response.data.date} an ${response.data.recipient} gesendet`);
                    }
                } else {
                    toast.error(response.data.message || 'Fehler beim Senden des Berichts');
                }
            } else {
                // For date ranges, send multiple requests
                const dateRange = [];
                const currentDate = new Date(startDate);

                while (currentDate <= endDate) {
                    dateRange.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                let successCount = 0;
                let failCount = 0;

                for (const date of dateRange) {
                    try {
                        const requestData = {
                            date: formatDate(date),
                            email: user.email
                        };

                        const response = await Api().post('/sendDailyEmailReport', requestData);

                        if (response.data.success) {
                            successCount++;
                        } else {
                            failCount++;
                            console.error(`Failed to send report for ${formatDate(date)}:`, response.data.message);
                        }
                    } catch (error) {
                        failCount++;
                        console.error(`Error sending report for ${formatDate(date)}:`, error);
                    }
                }

                if (successCount > 0) {
                    toast.success(`${successCount} E-Mail-Berichte erfolgreich gesendet`);
                }
                if (failCount > 0) {
                    toast.warning(`${failCount} Berichte konnten nicht gesendet werden`);
                }
            }
        } catch (error) {
            console.error('Error sending email report:', error);

            if (error.response?.status === 422) {
                const validationErrors = error.response.data.errors;
                if (validationErrors) {
                    Object.values(validationErrors).flat().forEach(errorMsg => {
                        toast.error(errorMsg);
                    });
                } else {
                    toast.error(error.response.data.message || 'Validierungsfehler');
                }
            } else if (error.response?.status === 500) {
                toast.error(error.response.data.message || 'Serverfehler beim Senden des Berichts');
                if (error.response.data.output) {
                    console.error('Server Output:', error.response.data.output);
                }
            } else {
                toast.error('Fehler beim Senden des E-Mail-Berichts');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!hasAccess) {
        return (
            <div className='dashboardContainer'>
                <div className='lg:flex justify-between pt-2 sm:block'>
                    <h2 className='text-2xl lg:text-left'>Email Reports</h2>
                </div>
                <div className='bg-white'>
                    <div className="rounded-xl p-8">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-600 mb-4">Zugriff verweigert</h2>
                            <p className="text-gray-500">Sie haben keine Berechtigung für diese Seite.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='dashboardContainer'>
            <div className='lg:flex justify-between pt-2 sm:block'>
                <h2 className='text-2xl lg:text-left'>Email Reports</h2>
            </div>

            <div className='bg-white'>
                <div className="rounded-xl p-8">
                    <div className="max-w-md mx-auto">
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Startdatum
                                </label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={setStartDate}
                                    dateFormat="dd.MM.yyyy"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholderText="Startdatum auswählen"
                                    maxDate={new Date()}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Enddatum
                                </label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={setEndDate}
                                    dateFormat="dd.MM.yyyy"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholderText="Enddatum auswählen"
                                    minDate={startDate}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-md">
                                <h3 className="font-medium mb-2">Berichtsdetails:</h3>
                                <p><strong>Empfänger:</strong> {user.email}</p>
                                <p><strong>Zeitraum:</strong> {startDate && endDate ? (
                                    startDate.getTime() === endDate.getTime()
                                        ? startDate.toLocaleDateString('de-DE')
                                        : `${startDate.toLocaleDateString('de-DE')} - ${endDate.toLocaleDateString('de-DE')}`
                                ) : 'Nicht ausgewählt'}</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <button
                            onClick={handleSendReport}
                            disabled={loading || !startDate || !endDate}
                            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                loading || !startDate || !endDate
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-mainBlue text-white hover:bg-lightBlue shadow shadow-md shadow-mainBlue'
                            }`}
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <BeatLoader size={8} color="white" className="mr-2" />
                                    E-Mail-Bericht wird gesendet...
                                </div>
                            ) : (
                                'E-Mail-Bericht senden'
                            )}
                            </button>
                        </div>

                        <div className="pt-4">
                            <h3 className="text-sm font-medium text-modalBackDropColor mb-2">Hinweise:</h3>
                            <ul className="text-sm text-modalBackDropColor space-y-1">
                                <li>• Die Berichte werden standardmäßig für gestern vorausgewählt</li>
                                <li>• Sie können einzelne Tage oder Datumsbereiche auswählen</li>
                                <li>• Bei Datumsbereichen wird für jeden Tag ein separater Bericht gesendet</li>
                                <li>• Die Berichte werden an Ihre E-Mail-Adresse gesendet: {user.email}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailReports;
